// src/modules/reports/services/reports.service.ts
import { AuthenticationEntity } from "@modules/auth/entities/auth.entity";
import {
  API_HEADER,
  POST_URL,
  REPORT_URL,
  USER_URL,
} from "@modules/shared/enums/common-enum";
import { fetchWithAuth } from "@modules/shared/helpers/fetch-with-auth.helper";
import { REPORT_STATUS, REPORT_TYPE } from "../enums/report.enum";
import {
  Report,
  ReportDetails,
  ReportDetailsWithUser,
  ResolveReport,
  ReportUserTarget,
  ReportTargetInfo,
} from "../models/report.model";

// --- Helpers ---
const getPostDetail = async (
  auth: AuthenticationEntity | null,
  setAuth: any,
  logout: any,
  postId: string,
) => {
  try {
    const res = await fetchWithAuth(
      `${POST_URL}/${postId}`,
      { method: "GET", headers: API_HEADER },
      auth,
      setAuth,
      logout,
    );
    return res.data;
  } catch {
    return null;
  }
};

const getUserDetail = async (
  auth: AuthenticationEntity | null,
  setAuth: any,
  logout: any,
  userId: string,
) => {
  try {
    const res = await fetchWithAuth(
      `${USER_URL}/${userId}`,
      { method: "GET", headers: API_HEADER },
      auth,
      setAuth,
      logout,
    );
    return res.data;
  } catch {
    return null;
  }
};

// Helper chuyển đổi user từ API sang ReportUserTarget
const transformUser = (user: any): ReportUserTarget | null => {
  if (!user) return null;
  if (!user.id && !user._id) return null;
  return {
    id: user.id || user._id,
    name: user.name || user.username || "Không xác định",
    username: user.username,
    avatar: user.avatar_url || user.avatar || user.avatars?.[0]?.url || null,
    email: user.email,
    phoneNumber: user.phoneNumber || user.phone,
    gender: user.gender,
    dateOfBirth: user.dateOfBirth || user.dob,
    bio: user.bio,
    role: user.role,
  };
};

// Gắn thông tin reporter vào một detail
const attachReporterToDetail = async (
  detail: ReportDetails,
  auth: AuthenticationEntity | null,
  setAuth: any,
  logout: any,
): Promise<ReportDetailsWithUser> => {
  if (!detail.reporter_id) return detail;
  try {
    const user = await getUserDetail(auth, setAuth, logout, detail.reporter_id);
    if (user) {
      const transformed = transformUser(user);
      if (transformed) {
        return { ...detail, reporter: transformed };
      }
    }
    return detail;
  } catch {
    return detail;
  }
};

// --- Service ---
export class ReportService {
  private getHeaders(): HeadersInit {
    return { ...API_HEADER };
  }

  async resolveReport(
    auth: AuthenticationEntity | null,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void,
    data: ResolveReport,
  ) {
    await fetchWithAuth(
      REPORT_URL,
      {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify({ ...data }),
      },
      auth,
      setAuth,
      logout,
    );
  }

  async getAllReportWithFilter(
    auth: AuthenticationEntity | null,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void,
    limit: number,
    page: number,
    status: REPORT_STATUS | "" = "",
    reportType: REPORT_TYPE | "" = "",
    search: string = "",
  ) {
    const query = new URLSearchParams();
    if (status) query.set("status", status);
    if (reportType) query.set("report_type", reportType);
    if (search) query.set("search", search);
    query.set("page", String(page));
    query.set("limit", String(limit));

    const res = await fetchWithAuth(
      `${REPORT_URL}?${query.toString()}`,
      { method: "GET", headers: this.getHeaders() },
      auth,
      setAuth,
      logout,
    );

    const reports = (res.data || []) as any[];

    for (const report of reports) {
      report.id = report._id || report.id;
      const type = report.report_type;
      let display: string | null = null;
      let userName: string | null = report.target_user_name || null;
      let targetData: ReportUserTarget | ReportTargetInfo | null = null;

      if (type === REPORT_TYPE.POST) {
        if (report.target_info) {
          targetData = report.target_info;
          display =
            report.target_info.title || report.target_info.content || null;
        }
        if (report.user_info?.target) {
          const owner = report.user_info.target;
          userName = owner.name || owner.username || userName;
        }
      } else if (
        type === REPORT_TYPE.PROFILE ||
        type === REPORT_TYPE.FEEDBACK
      ) {
        if (report.user_info?.target) {
          const user = report.user_info.target;
          const transformed = transformUser(user);
          targetData = transformed;
          display = transformed?.name || transformed?.username || null;
          userName = transformed?.name || transformed?.username || userName;
        }
      } else if (type === REPORT_TYPE.COMMENT) {
        if (report.target_info) {
          targetData = report.target_info;
          display = report.target_info.content || null;
        }
      }

      report.target_display = display || "Đã xóa";
      report.target_user_name =
        userName || report.target_user_name || "Không xác định";
      report.target = targetData;

      if (!Array.isArray(report.report_details)) {
        report.report_details = [];
      }
    }

    // Lấy thông tin người báo cáo đầu tiên
    const reporterIds = new Set<string>();
    for (const report of reports) {
      const details = report.report_details || [];
      if (details.length > 0 && details[0].reporter_id) {
        reporterIds.add(details[0].reporter_id);
      }
    }

    const userCache = new Map<string, any>();
    for (const id of Array.from(reporterIds)) {
      try {
        const user = await getUserDetail(auth, setAuth, logout, id);
        if (user) userCache.set(id, user);
      } catch {
        // im lặng
      }
    }

    for (const report of reports) {
      const details = report.report_details || [];
      if (details.length > 0 && details[0].reporter_id) {
        const user = userCache.get(details[0].reporter_id);
        if (user) {
          const transformed = transformUser(user);
          report.reporter_name = transformed?.name || "Không xác định";
        } else {
          report.reporter_name = "Không xác định";
        }
      } else {
        report.reporter_name = "Không xác định";
      }
    }

    res.data = reports;
    return res;
  }

  async getReportDetailById(
    auth: AuthenticationEntity | null,
    setAuth: (auth: AuthenticationEntity | null) => void,
    logout: () => void,
    targetId: string,
  ): Promise<Report> {
    let data;
    try {
      const res = await fetchWithAuth(
        `${REPORT_URL}/${targetId}`,
        { method: "GET", headers: this.getHeaders() },
        auth,
        setAuth,
        logout,
      );
      data = res.data;
    } catch {
      throw new Error("Không thể tải chi tiết báo cáo");
    }

    if (!data) {
      throw new Error("Không tìm thấy báo cáo");
    }

    let targetDisplay: string | null = null;
    let targetData: any = null;
    const type = data.report_type;

    try {
      if (type === REPORT_TYPE.POST && data.target_id) {
        const postData = await getPostDetail(
          auth,
          setAuth,
          logout,
          data.target_id,
        );
        if (postData) {
          targetData = postData;
          targetDisplay = postData.title || postData.content || "Đã xóa";
        } else {
          targetDisplay = "Đã xóa";
        }
      } else if (
        type === REPORT_TYPE.PROFILE ||
        type === REPORT_TYPE.FEEDBACK
      ) {
        const userId = data.target_user_id || data.target_id;
        if (userId) {
          const userData = await getUserDetail(auth, setAuth, logout, userId);
          if (userData) {
            targetData = transformUser(userData);
            targetDisplay =
              targetData?.name || targetData?.username || "Đã xóa";
          } else {
            targetDisplay = "Đã xóa";
          }
        } else {
          targetDisplay = "Đã xóa";
        }
      } else if (type === REPORT_TYPE.COMMENT) {
        // Lấy thông tin comment và bài viết cha
        const commentData = data.target_info || {
          content: data.target_content || "Đã xóa",
        };
        targetData = commentData;
        targetDisplay = commentData.content || "Đã xóa";
        // Nếu có post_id, lấy thêm bài viết
        if (commentData.post_id) {
          const postData = await getPostDetail(
            auth,
            setAuth,
            logout,
            commentData.post_id,
          );
          if (postData) {
            targetData = { ...commentData, post: postData };
            targetDisplay = `"${commentData.content}" - trong bài viết: "${postData.title || postData.content}"`;
          }
        }
      } else {
        targetDisplay = "Đã xóa";
      }
    } catch {
      targetDisplay = "Đã xóa";
    }

    const rawDetails = data.report_details || [];
    const baseDetails: ReportDetails[] = rawDetails.map(
      (d: any): ReportDetails => ({
        id: d._id,
        reporter_id: d.reporter_id,
        reason: d.reason,
        attachment: d.attachment || [],
        created_at: d.created_at ? new Date(d.created_at) : new Date(),
        updated_at: d.updated_at ? new Date(d.updated_at) : new Date(),
        deleted_at: d.deleted_at ? new Date(d.deleted_at) : null,
      }),
    );

    const reportDetailsWithUser = await Promise.all(
      baseDetails.map((detail) =>
        attachReporterToDetail(detail, auth, setAuth, logout),
      ),
    );

    return {
      id: data._id,
      target_id: data.target_id,
      target_user_id: data.target_user_id,
      report_type: data.report_type,
      resolver_id: data.resolver_id,
      admin_note: data.admin_note,
      status: data.status,
      action_taken: data.action_taken,
      created_at: new Date(data.created_at),
      updated_at: new Date(data.updated_at),
      deleted_at: data.deleted_at ? new Date(data.deleted_at) : null,
      target_user_name: data.target_user_name,
      target_display: targetDisplay || "Đã xóa",
      target: targetData,
      report_details: reportDetailsWithUser,
    };
  }
}
